import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from "@heroicons/react/solid";
import React, { useCallback, useRef } from "react";
import { useDrag, useDrop } from 'react-dnd';
import { classNames } from "../../../../../helpers/classNames";
import { LuGripVertical } from "react-icons/lu";
import SearchableColumnSelect from "../../shared/SearchableColumnSelect";

const directionOptions = {
  single_select: ["A → Z", "Z → A"],
  multi_select: ["A → Z", "Z → A"],
  checkbox: ["Checked → Empty", "Empty → Checked"],
  household_link: ["A → Z", "Z → A"],
  link: ["A → Z", "Z → A"],
  claim_household: ["A → Z", "Z → A"],
  text: ["A → Z", "Z → A"],
  date: ["Oldest → Newest", "Newest → Oldest"],
  due_date: ["Oldest → Newest", "Newest → Oldest"],
  number: ["Largest → Smallest", "Smallest → Largest"],
  advisor: ["A → Z", "Z → A"],
  default: ["Ascending", "Descending"]
};

const SmartTableSortsRow = ({
  allSorts,
  onSortsChange,
  columns,
  sort,
  thisSortIndex,
  moveSort,
}) => {
  const ref = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: 'sort',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = thisSortIndex;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveSort(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'sort',
    item: () => {
      return { id: sort.col_id, index: thisSortIndex };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  const handleUpdateCurrentSort = useCallback((updatedData) => {
    const newSorts = allSorts.map((s, index) =>
      index === thisSortIndex ? { ...s, ...updatedData } : s
    );
    onSortsChange(newSorts);
  }, [allSorts, thisSortIndex, onSortsChange]);

  const handleRemoveSort = useCallback(() => {
    const newSorts = allSorts.filter((_, index) => index !== thisSortIndex);
    onSortsChange(newSorts);
  }, [allSorts, thisSortIndex, onSortsChange]);

  const matchingColumn = columns.find(col => col.col_id === sort.col_id);
  const colType = matchingColumn?.col_type || 'text';

  const nonSortableColumns = ["button", "copy_to_clipboard"];

  // Get direction options for this column type
  const currentDirectionOptions = directionOptions[colType] || directionOptions.default;

  return (
    <div
      ref={ref}
      style={{ opacity }}
      data-handler-id={handlerId}
      className={classNames(
        "transition-opacity duration-200",
        isDragging && "cursor-grabbing"
      )}
    >
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="flex items-stretch divide-x divide-gray-200">
          {/* Sort Order Indicator */}
          <div className="px-2 py-2 flex items-center justify-center">
            <div className="w-5 h-5 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-semibold">
              {thisSortIndex + 1}
            </div>
          </div>

          {/* Sort Label */}
          <div className="px-2 py-2 flex items-center justify-center min-w-[60px] bg-gray-50">
            <p className="text-xs font-medium text-gray-700">
              {thisSortIndex === 0 ? "Sort by" : "Then by"}
            </p>
          </div>

          {/* Drag Handle */}
          <div
            className="px-2 py-2 cursor-grab hover:bg-gray-50 flex items-center justify-center transition-colors duration-150"
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <LuGripVertical className="w-4 h-4 text-gray-400" />
          </div>

          {/* Column Selection */}
          <div className="flex-1 min-w-[200px] overflow-visible">
            <SearchableColumnSelect
              columns={columns || []}
              value={sort.col_id}
              onChange={(col_id) => {
                const newColDef = columns.find(c => c.col_id === col_id);
                const newColType = newColDef?.col_type || 'text';
                const newDirection = newColType === colType ? sort.direction : 'asc';
                handleUpdateCurrentSort({ col_id, direction: newDirection });
              }}
              excludeTypes={nonSortableColumns}
              placeholder="Select column to sort..."
              className="border-none shadow-none rounded-none focus:ring-0 focus:border-transparent text-sm"
            />
          </div>

          {/* Direction Selection */}
          <div className="flex-1 min-w-[180px] px-2 py-1 overflow-visible">
            <div className="flex space-x-1">
              <button
                onClick={() => handleUpdateCurrentSort({ direction: "asc" })}
                className={classNames(
                  "flex-1 flex items-center justify-center px-2 py-1 rounded text-xs font-medium transition-colors duration-150 min-w-0",
                  sort.direction === "asc"
                    ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
                    : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                )}
              >
                <ArrowUpIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">{currentDirectionOptions[0]}</span>
              </button>
              <button
                onClick={() => handleUpdateCurrentSort({ direction: "desc" })}
                className={classNames(
                  "flex-1 flex items-center justify-center px-2 py-1 rounded text-xs font-medium transition-colors duration-150 min-w-0",
                  sort.direction === "desc"
                    ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
                    : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                )}
              >
                <ArrowDownIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">{currentDirectionOptions[1]}</span>
              </button>
            </div>
          </div>

          {/* Delete Button */}
          <div className="flex items-center">
            <button
              onClick={handleRemoveSort}
              className="px-3 py-2 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-150 h-full"
              title="Remove sort condition"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartTableSortsRow;
